import SidebarCollapse from "@builder/components/app/SidebarCollapse";
import { EditableProps } from "@builder/components/editable/editableProps";
import SelectInput from "@builder/components/general/SelectInput";
import SliderInput from "@builder/components/general/SliderInput";
import TwoDSlider from "@builder/components/general/TwoDSlider";
import UnitInput from "@builder/components/general/UnitInput";
import { getValueUnit } from "@builder/components/general/editUtils";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { updateNodeProperty } from "@builder/store/slices/BuilderSlice/builderSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { ImageNode } from "@common/build/types";
import "./style.scss";
import NumericInput from "@builder/components/general/NumericInput";
import { useEffect } from "react";
import { getStyle, getStylePath } from "@builder/components/editable/helpers";

const extractTransformValues = (transform: string = ""): { scale?: string; translate?: string } => {
    const scaleMatch = transform?.match(/scale\([^)]+\)/);
    const translateMatch = transform?.match(/translate\([^)]+\)/);

    return {
        scale: scaleMatch ? scaleMatch[0] : undefined,
        translate: translateMatch ? translateMatch[0] : undefined
    };
};

const ImageStyle = ({ parent, stateMode }: EditableProps) => {
    const dispatch = useAppDispatch();
    const node = useAppSelector(getSelectedNode) as ImageNode;
    const cvMode = useAppSelector(state => state.builder.conditionalVariationMode);
    const cvModeIndex = cvMode?.id === node?.id ? cvMode.index : null;

    const style = getStyle(node, undefined, stateMode, cvModeIndex);
    const stylePath = getStylePath(undefined, stateMode, cvModeIndex);

    const imageStyle = getStyle(node, ["image", "style"], stateMode, cvModeIndex);
    const imageStylePath = getStylePath(["image", "style"], stateMode, cvModeIndex);

    useEffect(() => {
        if (stateMode) {
            if (imageStyle.stateStyle?.objectFit === "cover") {
                if (imageStyle.stateStyle?.transform) {
                    dispatch(updateNodeProperty({ propertyPath: [...imageStylePath, "transform"], value: undefined }));
                }
            }
        } else {
            if (imageStyle.default?.objectFit === "cover") {
                if (imageStyle.default?.transform) {
                    dispatch(updateNodeProperty({ propertyPath: [...imageStylePath, "transform"], value: undefined }));
                }
            }
        }
    }, [imageStyle.default?.objectFit, imageStyle.stateStyle?.objectFit]);

    if (!node) return null;

    const imageFit = imageStyle.stateStyle?.objectFit || imageStyle.default?.objectFit;
    const imageFitOptions = {
        contain: "Contain",
        cover: "Cover"
    };
    const imageFitCallback = (value: string) => {
        dispatch(updateNodeProperty({ propertyPath: [...imageStylePath, "objectFit"], value }));
    };

    const widthString = (style.stateStyle?.width || style.default?.width) as string;
    const width = getValueUnit(widthString);
    const widthCallback = (value: string, unit: string) => {
        dispatch(
            updateNodeProperty({
                propertyPath: [...stylePath, "width"],
                value: value + unit,
                remove: !value
            })
        );
    };

    const maxWidthString = (style.stateStyle?.maxWidth || style.default?.maxWidth) as string;
    const maxWidth = getValueUnit(maxWidthString);
    const maxWidthCallback = (value: string, unit: string) => {
        dispatch(
            updateNodeProperty({
                propertyPath: [...stylePath, "maxWidth"],
                value: value + unit,
                remove: !value
            })
        );
    };

    const heightString = (style.stateStyle?.height || style.default?.height) as string;
    const height = getValueUnit(heightString);
    const heightCallback = (value: string, unit: string) => {
        dispatch(
            updateNodeProperty({
                propertyPath: [...stylePath, "height"],
                value: value + unit,
                remove: !value
            })
        );
    };

    const opacityString = (imageStyle.stateStyle?.opacity || imageStyle.default?.opacity) as string;
    const opacity = getValueUnit(opacityString);
    const opacityCallback = (value: string, unit: string) => {
        dispatch(
            updateNodeProperty({
                propertyPath: [...imageStylePath, "opacity"],
                value: value + unit
            })
        );
    };

    const transform = extractTransformValues(imageStyle.stateStyle?.transform || imageStyle.default?.transform);
    const backgroundImage = node.image?.url;

    const scale = transform.scale ? (transform.scale === "scale(2.0)" ? "100" : transform.scale.replace("scale(1.", "").replace(")", "")) : undefined;
    const scaleCallback = (value: string) => {
        const scaleValue = value === "100" ? `scale(2.0)` : `scale(1.${value})`;
        dispatch(updateNodeProperty({ propertyPath: [...imageStylePath, "transform"], value: [scaleValue, transform.translate].join(" ") }));
    };

    const position = transform.translate ? transform.translate.replace("translate(", "").replace(")", "").replace(",", "").split(" ") : undefined;
    const xPos = position ? parseInt(position[0].replace("%", "")) : 0;
    const yPos = position ? parseInt(position[1].replace("%", "")) : 0;

    const positionCallback = (x: number, y: number) => {
        dispatch(
            updateNodeProperty({
                propertyPath: [...imageStylePath, "transform"],
                value: [transform.scale, `translate(${x}%, ${y}%)`].join(" ")
            })
        );
    };

    const handleReset = () => {
        dispatch(
            updateNodeProperty({
                propertyPath: [...imageStylePath, "transform"],
                value: [transform.scale, `translate(${0}%, ${0}%)`].join(" ")
            })
        );
    };

    const xPosCallback = (value: number) => {
        dispatch(
            updateNodeProperty({
                propertyPath: [...imageStylePath, "transform"],
                value: [transform.scale, `translate(${Math.min(value || 0, 100)}%, ${yPos}%)`].join(" ")
            })
        );
    };

    const yPosCallback = (value: number) => {
        dispatch(
            updateNodeProperty({
                propertyPath: [...imageStylePath, "transform"],
                value: [transform.scale, `translate(${xPos}%, ${Math.min(value || 0, 100)}%)`].join(" ")
            })
        );
    };

    return (
        <SidebarCollapse title="Image" parent={parent}>
            <SelectInput title="Image fit" value={imageFit || "contain"} options={imageFitOptions} callback={imageFitCallback} />
            {imageFit === "contain" && (
                <>
                    <SliderInput title="Zoom" value={scale} unit="%" callback={scaleCallback} min={0} max={100} unitMin={0} unitMax={100} />
                    <TwoDSlider
                        x={xPos}
                        y={yPos}
                        min={-100}
                        max={100}
                        callback={positionCallback}
                        title="Image position"
                        reset={handleReset}
                        backgroundImage={backgroundImage}
                        reversed
                    />
                    <div className="double-input">
                        <NumericInput title="Horizontal" value={xPos} callback={xPosCallback} direction="column" />
                        <NumericInput title="Vertical" value={yPos} callback={yPosCallback} direction="column" />
                    </div>
                </>
            )}

            <UnitInput title="Width" {...width} callback={widthCallback} />
            <UnitInput title="Max width" {...maxWidth} callback={maxWidthCallback} />
            <UnitInput title="Height" {...height} callback={heightCallback} />
            <SliderInput title="Opacity" value={opacity.value} unit="%" callback={opacityCallback} min={0} max={100} unitMin={0} unitMax={100} />
        </SidebarCollapse>
    );
};

export default ImageStyle;
